import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type { CombatPowerResponse } from '../../../types/combat-power.js';
import {
  TARGET_OUTLINE_COLOR,
  CURRENT_TARGET_COLOR,
  actualBarColor,
  aggregateMembers,
  BudgetBarChart,
} from './BudgetBarChart.js';
import type { ChartItem } from './BudgetBarChart.js';

/** @graph-connects none */
export const ACE_SUB_TEAMS = [
  { key: 'aircloset', label: 'airCloset', team: 'airCloset team' },
  { key: 'platform', label: 'Platform', team: 'Platform team' },
  { key: 'mall', label: 'Mall', team: 'Mall team' },
  { key: 'qa', label: 'QA', team: 'QA team' },
] as const;

/** @graph-connects none */
function AceLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
      <span className="flex items-center gap-1">
        <span
          className="inline-block w-4 h-3 border border-dashed rounded-sm"
          style={{ borderColor: TARGET_OUTLINE_COLOR }}
        />
        {t('members.combatPower.targetFullLabel')}
      </span>
      <span className="flex items-center gap-1">
        <span
          className="inline-block w-4 h-3 rounded-sm"
          style={{ backgroundColor: CURRENT_TARGET_COLOR }}
        />
        {t('members.combatPower.currentTargetLabel')}
      </span>
    </div>
  );
}

/** @graph-connects none */
export function AceTeamsChart({
  data,
  memberTeamMap,
  currentMonthIdx,
}: {
  data: CombatPowerResponse;
  memberTeamMap: Map<string, { group: string; team: string }>;
  currentMonthIdx: number;
}) {
  const { t } = useTranslation();
  const currentMonth = data.monthHeaders[currentMonthIdx] ?? '';

  const teams: ChartItem[] = ACE_SUB_TEAMS.map((st) => {
    const mems = data.members.filter((m) => {
      const info = memberTeamMap.get(m.name.toLowerCase());
      return info?.group === 'ACE' && info.team === st.team;
    });
    const agg = aggregateMembers(mems, currentMonthIdx);
    return {
      label: st.label,
      actual: agg.actual,
      actualInProgress: agg.actualInProgress,
      target: agg.capacity,
      currentTarget: agg.target,
    };
  });

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={`ACE ${t('members.combatPower.budgetChart')} (${currentMonth})`}>
        <AceLegend />
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className="flex-1 flex gap-3 flex-col lg:flex-row">
            {teams.map((item) => (
              <div key={item.label} className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-[var(--color-text-primary)] mb-1 text-center">
                  {item.label}
                </h3>
                <div className="h-40">
                  <BudgetBarChart
                    items={[item]}
                    height={160}
                    barSize={44}
                    showXAxis={false}
                    showLegend={false}
                  />
                </div>
                <div className="text-center text-xs text-[var(--color-text-muted)] mt-1">
                  {t('members.combatPower.progressRate')}:{' '}
                  <span
                    className="font-bold"
                    style={{
                      color: actualBarColor(
                        item.actual + item.actualInProgress,
                        item.currentTarget,
                      ),
                    }}
                  >
                    {item.currentTarget > 0
                      ? `${(((item.actual + item.actualInProgress) / item.currentTarget) * 100).toFixed(1)}%`
                      : '-'}
                  </span>
                  <span className="ml-1">
                    (
                    {item.actualInProgress > 0
                      ? `${item.actual.toLocaleString()}(${item.actualInProgress.toLocaleString()})`
                      : item.actual.toLocaleString()}
                    /{item.currentTarget.toLocaleString()})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
